import * as React from 'react'
import { StoreProps } from '../../../services/stores/casting.store'
import { offeredServiceI18nInterventionPlace } from '../../../services/models/offered-services.service'
import { I18nService } from '../../../services/utils/i18n.service'
import CastingStepComponent from './casting-step.component'
import CastingCityInputComponent from '../../plugins/casting/city/casting-city-input.component'
import CastingPromotionalCodePickerComponent from '../../plugins/casting/promotional-code/casting-promotional-code-picker.component'
import CastingOfferedServiceDescriptionComponent from '../../plugins/casting/offered-service/casting-offered-service-description.component'
import SelectableListComponent from '../../shared/selectable-list.component'
import { useMountTooltipRef } from './casting-step-use-mount-tooltip-ref.hook'
import { CastingCurrentEmployeeProps } from '../../../services/json-schemas/serializers/casting-current-employee-props'
import FakeInputComponent from '../../shared/fake-input.component'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import styled from 'styled-components'

type Props = StoreProps & { btnOnClick: () => void, mobile?: boolean }

const LogoImg = styled.img<{ $fullWidth?: boolean, $height?: number }>`
  width: ${props => props.$fullWidth ? '100%' : 'auto'} !important;
  height: ${props => props.$height ?? 200}px !important;
  object-fit: contain;

  &:hover {
    opacity: 1 !important;
  }
`

const i18nScoped = new I18nService(require('./casting-step-1.component.yml'))

export default function CastingStep1Component ({ data, store, mobile, btnOnClick }: Props) {
  const { offeredService, prescriber, referrerCustomer, serviceDeliveryLocation, requiredPlace, market, backofficeSelectAssignableForSlots, enableLodging } = data

  /**
   * The market logo animation moves the inputRef that is used to anchor the `currentTooltip`. If a tooltip is displayed 
   * just before the end of the animation, the `currentTooltip` position is below the expected position.
   * 
   * This property is used as an update and a visibility key for `useMountTooltipRef` to re-trigger the `currentTooltip`
   *  display at the expected position.
   */
  const [tooltipPositionIsStable, setTooltipPositionIsStable] = React.useState(true)

  const { currentTooltip, mountTooltipRef } = useMountTooltipRef({ data, store, step: 1, tooltipPositionIsStable })
  const hasPromotionalCodes = !prescriber && offeredService && offeredService['has_active_promotional_codes?']

  const showPromotionalCodeInput = !offeredService?.casting_custom_price
    && market['promotional_code_ask_in_casting?']
    && hasPromotionalCodes
    && !offeredService?.selected_quantity_discount_for_customer

  const shouldDisplayLogo = !!market.logo?.url && !data.offeredService

  /**
   * If true, the current user is an employee and it must say "yes" or "no" to the question "assign the casting to yourslef?"
   * From that question depends the available offeredServices, among others.
   */
  const waitingEmployeeNotifyingSelfAssign = window.currentUser?.type === 'Employee' && backofficeSelectAssignableForSlots === null

  const deliveryLocationTitle = React.useMemo(() => {
    if (serviceDeliveryLocation) return <span>{ serviceDeliveryLocation.name } <strong className='u-txtClr1'>{ ` ${ serviceDeliveryLocation.distance }km` }</strong></span>
    if (requiredPlace) return `${store.getStepTitle('required-place')} : ${offeredServiceI18nInterventionPlace(requiredPlace)}`
    return null
  }, [requiredPlace, serviceDeliveryLocation, store])

  return <CastingStepComponent
    key="1"
    title={ <>
      <TransitionGroup>
        {/* When the animation is completed, the tooltip is displayed */}
        { shouldDisplayLogo && <CSSTransition
          classNames="castingStepLogo-anim"
          timeout={{ enter: 500, exit: 500 }}
          onEnter={ () => setTooltipPositionIsStable(false) }
          onEntered={ () => setTooltipPositionIsStable(true) }
          onExit={ () => setTooltipPositionIsStable(false) }
          onExited={ () => setTooltipPositionIsStable(true) }
        >
          <div>
            { !prescriber && <LogoImg className="headerMain-logo" $fullWidth src={ market.logo.url } alt={ market.name } /> }
            { prescriber && <>
              <LogoImg className="headerMain-logo" $fullWidth $height={ 150 } src={ prescriber.logo.url } alt={ prescriber.name } />
              <div className="u-gridInline-hMiddle u-alignMiddle u-mts u-mbs">
                <span>{ i18nScoped.t('header.operated_by') }</span>
                <LogoImg className="headerMain-logo u-mls" $height={ 50 } src={ market.logo.url } alt={ market.name } />
              </div>
            </> }
            { referrerCustomer && <div className="u-gridInline-hMiddle u-txtLight u-alignMiddle u-mts u-mbm">{ i18nScoped.t('header.referral_by', { name: referrerCustomer.name }) }</div> }
          </div>
        </CSSTransition> }
      </TransitionGroup>
      { i18nScoped.t('title') }
    </> }
    btnText={ i18nScoped.t('next') }
    btnOnClick={ btnOnClick }
    btnDisabled={ !store.validatesLegacyCastingStep(1) }
    className={ (mobile ? 'mobile' : '') + ' js-casting-step-1' }
    tooltip={ mobile ? undefined : currentTooltip }>

    { /** Asks to the employee if the casting is assigned to himself or not. If self assigned, a proposal i immediately sent to the customer. */ }
    { window.currentUser?.type === 'Employee' && <SelectableListComponent
      className="u-mbs"
      name="employeeSelfAssignment"
      data={ [true, false] }
      value={ backofficeSelectAssignableForSlots }
      formatter={ value => i18nScoped.t(value ? 'employee_self_assignment_true' : 'employee_self_assignment_false') }
      onChange={ value => {
        const employee = { ...((window as any).currentEmployee as CastingCurrentEmployeeProps), name: i18nScoped.t('myself') }
        store.set({ backofficeSelectAssignableForSlots: value, backofficePreselectedEmployee: value ? employee : null, requiredEmployees: value ? [employee] : [] })
      } } /> }
    
    { !waitingEmployeeNotifyingSelfAssign && <React.Fragment>
      <CastingCityInputComponent
        store={ store }
        data={ data }
        mode="legacy-step-1"
        inputClassName="input--withIcon icon-location"
        hideCountrySelectDespiteWorldwide={ mobile }
        onFocus={ () => store.tryFocus('city') }
        hasErrors={ store.hasError('city') }
        ref={ mountTooltipRef('city') } />

      <FakeInputComponent
        placeholder={ store.getStepTitle('offered-service') }
        value={ offeredService?.name }
        onClick={ () => store.tryFocus('offered-service') }
        className={ 'js-test-fake-input-service ' + (store.hasError('service') ? 'has-errors' : '') }
        ref={ mountTooltipRef('offered-service') } />

      { store.canFocus('enable-lodging') && <button
        className="buttonNeutral button--narrower button--block u-mbs"
        children={ i18nScoped.t(`lodging_button.${enableLodging ? 'yes' : (enableLodging === null ? 'unset' : 'no')}`) }
        onClick={ () => store.tryFocus('enable-lodging') }
        ref={ mountTooltipRef('enable-lodging') } /> }

      { store.canFocus('lodging') && <div className="input--withIcon icon-building">
        <FakeInputComponent
          placeholder={  store.getStepTitle('lodging') }
          value={ store.getSummaryForStep('lodging') === '-' ? null : store.getSummaryForStep('lodging') }
          onClick={ () => store.tryFocus('lodging') }
          className={ 'js-test-fake-input-lodging ' + (store.hasError('lodging') ? 'has-errors' : '') }
          ref={ mountTooltipRef('lodging') } />
      </div> }

      { /** Asks combination attributes (part of rentable products) that tare expected to be rendered right after the offered_service (other attributes may be asked later) */ }
      <div style={ store.canFocus('combination-attributes-after-offered-service') ? undefined : { display: 'none' } } className="input--withIcon icon-tags">
        <FakeInputComponent
          placeholder={ store.getStepTitle('combination-attributes-after-offered-service') }
          value={ store.getSummaryForStep('combination-attributes-after-offered-service') === '-' ? null : store.getSummaryForStep('combination-attributes-after-offered-service') }
          onClick={ () => store.tryFocus('combination-attributes-after-offered-service') }
          ref={ mountTooltipRef('combination-attributes-after-offered-service') } />
      </div>

      { store.canFocus('preferences') && <FakeInputComponent
        placeholder={ store.getStepTitle('preferences') }
        value={ store.getSummaryForStep('preferences') === '-' ? null : store.getSummaryForStep('preferences') }
        onClick={ () => store.tryFocus('preferences') }
        className={ 'js-test-fake-input-preferences ' + (store.hasError('preferences') ? 'has-errors' : '') }
        ref={ mountTooltipRef('preferences') } /> }
      
      { /** Asks the required place and the service_delivery_location if on_site has been selected */ }
      <div style={ store.canFocus('required-place') ? undefined : { display: 'none' } }>
        <FakeInputComponent
          placeholder={ store.getStepTitle('required-place') }
          value={ deliveryLocationTitle }
          onClick={ () => store.tryFocus('required-place') }
          className={ 'js-test-fake-service-delivery-locations ' + (store.hasError('service_delivery_location') ? 'has-errors' : '') }
          ref={ mountTooltipRef('required-place') } />
      </div>

      { showPromotionalCodeInput && <CastingPromotionalCodePickerComponent store={ store } data={ store.data } /> }
      { offeredService && <CastingOfferedServiceDescriptionComponent store={ store } data={ store.data } /> }
    </React.Fragment> }
  </CastingStepComponent>
}
